import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Autocomplete, Box, MenuItem, createFilterOptions } from "@mui/material";
import { C } from "../constants";
import { IPD_COLORS, ipdCardSx } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import IpdDataGrid from "../../ipd/utils/IpdDataGrid";
import SvgIcon from "../SvgIcon";
import {
  listLisMappings,
  listLisTests,
  rejectLisMappings,
  runLisAutomap,
  verifyLisMappings,
} from "../lis-mapping-api";

// LIS test mapping — each lab service's code at the facility's LIS partner.
// Only verified codes are sent with samples; automap fills exact matches as
// verified and the rest as suggestions a person accepts, changes or rejects
// here. Full screen swapped in place of the Service Master grid.

const STATUS_META = {
  verified: { label: "Verified", bg: IPD_COLORS.success.bg, color: IPD_COLORS.success.text },
  suggested: { label: "Suggested", bg: IPD_COLORS.warning.bg, color: IPD_COLORS.warning.text },
  partial: { label: "Partly mapped", bg: IPD_COLORS.warning.bg, color: IPD_COLORS.warning.text },
  rejected: { label: "Rejected", bg: IPD_COLORS.error.bg, color: IPD_COLORS.error.text },
  unmapped: { label: "Not mapped", bg: IPD_COLORS.pageBg, color: IPD_COLORS.textSecondary },
};

const FILTERS = [
  { id: "review", label: "Needs review", test: (r) => r.status === "suggested" || r.status === "partial" },
  { id: "check", label: "Check carefully", test: (r) => (r.status === "suggested" || r.status === "partial") && r.checkWords.length > 0 },
  { id: "unmapped", label: "Not mapped", test: (r) => r.status === "unmapped" },
  { id: "verified", label: "Verified", test: (r) => r.status === "verified" },
  { id: "rejected", label: "Rejected", test: (r) => r.status === "rejected" },
  { id: "all", label: "All tests", test: () => true },
];

const isReviewable = (r) => Boolean(r.mapping) && (r.status === "suggested" || r.status === "partial");

const SOURCE_LABEL = { exact: "Exact name", fuzzy: "Similar name", manual: "Picked by hand" };

// The partner list has ~1,300 tests; render only the first matches while typing.
const filterLisTests = createFilterOptions({ limit: 100 });
const lisTestLabel = (t) => (t.code === t.name ? t.code : `${t.name} — ${t.code}`);

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.unmapped;
  return (
    <Box component="span" sx={{ padding: "2px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap", background: meta.bg, color: meta.color }}>
      {meta.label}
    </Box>
  );
}

// ── Pick a code by hand ───────────────────────────────────────────────────────
function PickCodeModal({ row, tests, testsError, onClose, onSave }) {
  // Kept as the code so the current mapping is preselected once the list arrives.
  const [pickedCode, setPickedCode] = useState(row.mapping?.testCode ?? null);
  const picked = useMemo(() => (tests || []).find((t) => t.code === pickedCode) || null, [tests, pickedCode]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!picked) return;
    setSaving(true);
    const ok = await onSave(picked.code);
    if (!ok) setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "560px", maxWidth: "100%", background: IPD_COLORS.cardBg, borderRadius: "12px", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "15px", fontWeight: 800, color: C.text }}>Pick LIS test</span>
          <IpdButton variant="icon" onClick={onClose} sx={{ minWidth: 0 }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </IpdButton>
        </div>
        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 700, color: C.text }}>{row.name}</div>
            <div style={{ fontSize: "11px", color: C.textSub }}>{row.uniqueCode} · {row.bedTypes.join(", ")}</div>
          </div>
          <IpdFormField label="LIS test">
            <Autocomplete
              options={tests || []}
              value={picked}
              onChange={(_, t) => setPickedCode(t ? t.code : null)}
              getOptionLabel={lisTestLabel}
              isOptionEqualToValue={(a, b) => a.code === b.code}
              filterOptions={filterLisTests}
              loading={!tests && !testsError}
              size="small"
              autoFocus
              renderInput={(params) => (
                <IpdTextField {...params} placeholder="Search by test name or code…" error={Boolean(testsError)} helperText={testsError || undefined} />
              )}
            />
          </IpdFormField>
          <div style={{ fontSize: "11px", color: C.textSub }}>
            Saving marks this code as verified for every bed type of the test. Samples are sent with this code from now on.
          </div>
        </div>
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <IpdButton variant="outlined" onClick={onClose}>Cancel</IpdButton>
          <IpdButton onClick={save} loading={saving} disabled={!picked}>Save as verified</IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────────
export default function LisMappingScreen({ onBack }) {
  const [partner, setPartner] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("review");
  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState([]);
  const [busy, setBusy] = useState(false);
  const [automapping, setAutomapping] = useState(false);
  const [picking, setPicking] = useState(null);
  const [lisTests, setLisTests] = useState(null);
  const [lisTestsError, setLisTestsError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await listLisMappings();
      setPartner(res.partner);
      setRows(res.rows);
    } catch (err) {
      setLoadError(err.message || "Could not load the LIS mapping");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The partner's test list is only needed to pick by hand: load it the first time.
  useEffect(() => {
    if (!picking || lisTests) return;
    let cancelled = false;
    setLisTestsError("");
    listLisTests()
      .then((tests) => { if (!cancelled) setLisTests(tests); })
      .catch((err) => { if (!cancelled) setLisTestsError(err.message || "Could not load the LIS test list"); });
    return () => { cancelled = true; };
  }, [picking, lisTests]);

  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.id, rows.filter(f.test).length])),
    [rows],
  );

  const visible = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    const q = search.trim().toLowerCase();
    return rows.filter(f.test).filter((r) => {
      if (!q) return true;
      return [r.name, r.uniqueCode, r.mapping?.testCode, r.mapping?.testName].some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [rows, filter, search]);

  // Runs a write, reloads, and reports. Resolves true when it worked.
  const act = async (write) => {
    setBusy(true);
    try {
      const message = await write();
      toast.success(message);
      setSelection([]);
      await load();
      return true;
    } catch (err) {
      toast.error(err.message || "Could not save");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const accept = (targets) =>
    act(async () => {
      await verifyLisMappings(targets.map((r) => ({ serviceIds: r.serviceIds, testCode: r.mapping.testCode })));
      return targets.length === 1 ? `${targets[0].name} verified` : `${targets.length} tests verified`;
    });

  const reject = (row) =>
    act(async () => {
      await rejectLisMappings(row.serviceIds);
      return `${row.name}: suggestion rejected`;
    });

  const automap = async () => {
    setAutomapping(true);
    await act(async () => {
      const s = await runLisAutomap();
      return `Automap done — ${s.verified || 0} verified, ${s.suggested || 0} suggested, ${(s.unmatched || 0) + (s.removed || 0)} without a match`;
    });
    setAutomapping(false);
  };

  const savePicked = async (code) => {
    const ok = await act(async () => {
      await verifyLisMappings([{ serviceIds: picking.serviceIds, testCode: code }]);
      return `${picking.name} verified`;
    });
    if (ok) setPicking(null);
    return ok;
  };

  const selectedRows = rows.filter((r) => selection.includes(r.key) && isReviewable(r));

  const columns = [
    { field: "name", headerName: "Our test", flex: 1.4, minWidth: 200, sortable: false,
      renderCell: (p) => (
        <Box sx={{ py: "8px", minWidth: 0 }}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: C.text }}>{p.row.name}</div>
          <div style={{ fontSize: "11px", color: C.textSub }}>{p.row.uniqueCode} · {p.row.bedTypes.join(", ")}</div>
        </Box>
      ) },
    { field: "lis", headerName: "LIS test", flex: 1.6, minWidth: 220, sortable: false,
      renderCell: (p) => {
        const m = p.row.mapping;
        if (!m) return <span style={{ fontSize: "12px", color: C.textSub }}>—</span>;
        return (
          <Box sx={{ py: "8px", minWidth: 0 }}>
            {/* white-space: pre keeps the leading/trailing spaces some LIS codes have */}
            <div style={{ fontSize: "12px", fontFamily: "monospace", fontWeight: 700, color: C.primary, whiteSpace: "pre" }}>{m.testCode}</div>
            {m.testName && m.testName !== m.testCode && <div style={{ fontSize: "11px", color: C.textSub }}>{m.testName}</div>}
          </Box>
        );
      } },
    { field: "source", headerName: "Match", width: 130, sortable: false,
      renderCell: (p) => {
        const m = p.row.mapping;
        if (!m) return null;
        return (
          <span style={{ fontSize: "12px", color: C.textSub }}>
            {SOURCE_LABEL[m.source] || m.source}{m.source === "fuzzy" && m.score != null ? ` · ${Math.round(m.score)}` : ""}
          </span>
        );
      } },
    { field: "status", headerName: "Status", width: 170, sortable: false,
      renderCell: (p) => (
        <Box sx={{ py: "8px", display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
          <StatusPill status={p.row.status} />
          {p.row.status !== "verified" && p.row.checkWords.length > 0 && (
            <span title="These words differ between our test and the LIS test, which usually means a different test" style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: 700, color: IPD_COLORS.warning.text }}>
              <SvgIcon name="alert-triangle" size={12} color={IPD_COLORS.warning.text} />
              Check: {p.row.checkWords.join(", ")}
            </span>
          )}
        </Box>
      ) },
    { field: "actions", headerName: "", width: 250, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: "6px", whiteSpace: "nowrap" }}>
          {isReviewable(p.row) && (
            <IpdButton size="small" onClick={() => accept([p.row])} disabled={busy} startIcon={<SvgIcon name="check" size={13} color={IPD_COLORS.white} />}>
              Accept
            </IpdButton>
          )}
          <IpdButton variant="outlined" size="small" onClick={() => setPicking(p.row)} disabled={busy} startIcon={<SvgIcon name="edit" size={13} color={C.text} />}>
            {p.row.mapping ? "Change" : "Pick"}
          </IpdButton>
          {isReviewable(p.row) && (
            <IpdButton variant="outlined" size="small" onClick={() => reject(p.row)} disabled={busy} sx={{ color: IPD_COLORS.error.text, borderColor: IPD_COLORS.error.border }}>
              Reject
            </IpdButton>
          )}
        </Box>
      ) },
  ];

  return (
    <div style={{ animation: "ipdFadeIn 0.2s ease", display: "flex", flexDirection: "column", gap: "16px" }}>
      <style>{`@keyframes ipdFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>

      <div>
        <IpdButton variant="outlined" onClick={onBack} startIcon={<SvgIcon name="back" size={16} color={C.text} />}>
          Back to Service Master
        </IpdButton>
      </div>

      <Box sx={{ ...ipdCardSx }}>
        <div style={{ padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
            <span style={{ fontSize: "17px", fontWeight: 800, color: C.text }}>LIS test mapping</span>
            <span style={{ fontSize: "12px", color: C.textSub }}>
              {partner ? `Partner: ${partner.name} (${partner.code})` : "Loading partner…"}
            </span>
            <span style={{ fontSize: "12px", color: C.textSub, maxWidth: "70ch" }}>
              Only verified codes are sent with samples. Check every suggestion — the LIS accepts a wrong code without an error.
            </span>
            {partner && !partner.is_active && (
              <span style={{ fontSize: "12px", fontWeight: 700, color: IPD_COLORS.error.text }}>
                This partner is inactive: samples are not being sent.
              </span>
            )}
          </div>
          <IpdButton variant="outlined" onClick={automap} loading={automapping} disabled={busy || loading} startIcon={<SvgIcon name="sparkle" size={15} color={C.text} />}>
            Run automap
          </IpdButton>
        </div>
      </Box>

      <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
        <IpdTextField select value={filter} onChange={(e) => { setFilter(e.target.value); setSelection([]); }} sx={{ minWidth: 220 }}>
          {FILTERS.map((f) => <MenuItem key={f.id} value={f.id}>{f.label} ({counts[f.id] ?? 0})</MenuItem>)}
        </IpdTextField>
        <IpdTextField value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search test, code…" sx={{ minWidth: 260 }} />
        <div style={{ flex: 1 }} />
        <IpdButton onClick={() => accept(selectedRows)} disabled={busy || selectedRows.length === 0} startIcon={<SvgIcon name="check" size={15} color={IPD_COLORS.white} />}>
          Accept selected ({selectedRows.length})
        </IpdButton>
      </div>

      <IpdDataGrid
        rows={visible}
        columns={columns}
        loading={loading}
        error={loadError}
        onRetry={load}
        getRowId={(r) => r.key}
        getRowHeight={() => "auto"}
        checkboxSelection
        isRowSelectable={(p) => isReviewable(p.row)}
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        disableRowSelectionOnClick
        disableColumnMenu
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
        emptyMessage="No tests for this filter"
        emptyDescription="Pick another filter, or run automap to fill suggestions from the LIS test list."
        sx={{ border: "none" }}
      />

      {picking && (
        <PickCodeModal
          row={picking}
          tests={lisTests}
          testsError={lisTestsError}
          onClose={() => setPicking(null)}
          onSave={savePicked}
        />
      )}
    </div>
  );
}
